// The "What's new" changelog nudge on the home dashboard, as one unit: its state,
// when it shows, the changelog watcher it owns, and what happens when it is
// dismissed or tapped.
//
// This is the BACKWARD-looking card — "here is what changed in the version you
// are already on". Its forward-looking sibling, the store "Update available"
// prompt, shares the same dashboard slot and takes priority when both have
// something to say; that rule lives with every other notice-coexistence rule in
// cs-day-items-notice-stack.ts, so `wantsToShow` being true here does not by
// itself draw the card.
//
// Moved out of CsDayItemsComponent by #5236, unchanged. The gate itself is
// `shouldShowWhatsNewCard` in cs-day-items-changelog-card.ts, which is pure and
// separately tested.
//
// Angular-free on purpose — see cs-day-items-notice-host.ts for why.

import { shouldShowWhatsNewCard } from './cs-day-items-changelog-card';
import { DayItemsNoticeController, DayItemsNoticeHost, markNoticeHostForCheck } from './cs-day-items-notice-host';

/**
 * The app-wide flags this notice keeps truthful.
 *
 * They are read by code outside this card — the changelog page, and the update
 * card's yield-the-slot path — so the notice cannot simply keep the changelog
 * revision to itself. The component supplies this view of the global object
 * rather than the notice importing it, which keeps the notice testable.
 */
export interface WhatsNewGlobalFlags {
  getShowChangelogNotification(): boolean;
  setShowChangelogNotification(value: boolean): void;
  getChangelogRev(): string | undefined;
  setChangelogRev(rev: string): void;
}

/**
 * Reading the changelog document, and the profile the gate compares it against.
 *
 * Narrow on purpose: handing the notice the text and user database services would
 * pull `@angular/core` in through them and lose the mocha unit-test path.
 */
export interface WhatsNewChangelogReader {
  /**
   * Reads the changelog document's revision, through a watcher the host creates
   * on first use so a newer changelog syncing in later re-runs the gate.
   *
   * Throws when the document is not there yet or the read failed — the caller
   * decides which of those is worth reporting.
   */
  readChangelogRevAsync(onChangelogChanged: () => void): Promise<string>;
  /** Says whether a read failure is an expected, non-actionable kind — `isExpectedChangelogReadFailure`. */
  isExpectedReadFailure(err: unknown): boolean;
  /**
   * The revision this person has already seen, from their synced profile.
   *
   * Throws when the profile could not be read at all (df-9241e82523c2). A profile that
   * does not exist yet is not a failure: it answers `undefined`.
   */
  readLastSeenRevAsync(): Promise<string | undefined>;
  /** Records that this person has now seen the given revision. Throws when the profile could not be read or written. */
  saveLastSeenRevAsync(rev: string): Promise<void>;
  /**
   * Says whether a failure to read or write the profile is an expected, non-actionable
   * kind: a transient fetch error, or the database being torn down (sign-out, reload).
   */
  isExpectedProfileFailure(err: unknown): boolean;
  /** Releases the changelog watcher, if one was created. */
  release(): void;
}

export interface WhatsNewNoticeDeps {
  host: DayItemsNoticeHost;
  changelog: WhatsNewChangelogReader;
  flags: WhatsNewGlobalFlags;
  /** The version string the card names, e.g. "1.32.1". */
  readableAppVersion(): string;
  /** Reports a problem that the user is not interrupted about. */
  trackSilentProblem(name: string, context: Record<string, unknown>): void;
  /** Records that the person opened the changelog from this card. */
  trackWhatsNewOpened(version: string): void;
  /** Opens the changelog page. */
  openChangelogPage(): void;
}

/** The "What's new" changelog nudge. */
export class CsWhatsNewNotice implements DayItemsNoticeController {
  /** Whether this notice has something to say. Read by `decideVisibleNotices`. */
  wantsToShow = false;
  /** The version the card names. Only meaningful while the card shows. */
  version = '';

  constructor(private deps: WhatsNewNoticeDeps) {}

  /**
   * Recomputes whether the card should show.
   *
   * Done reactively: the reader registers a watcher on the changelog document, so
   * a newer changelog syncing in after the dashboard has loaded re-runs this and
   * surfaces the card without waiting for a cold start.
   *
   * Also keeps the app-wide flags truthful, because the dismissal write and the
   * update card's yield-the-slot path both read them.
   */
  async refreshAsync(): Promise<void> {
    let changelogRev: string;
    try {
      changelogRev = await this.deps.changelog.readChangelogRevAsync(() => { void this.refreshAsync(); });
    } catch (err) {
      // if the failure is an expected kind (isExpectedChangelogReadFailure lists them),
      // leave the card state untouched. the watcher re-runs this when the document
      // syncs in or a database recovery finishes; if the document was already local
      // and nothing changes it, the card waits for the next dashboard load.
      // anything else is unexpected and must surface (CLAUDE.md "unexpected
      // conditions must surface")
      if (this.deps.changelog.isExpectedReadFailure(err)) {
        return;
      }
      this.deps.trackSilentProblem('WhatsNewCardChangelogLoadFailed', {
        name: (err as { name?: string })?.name,
        message: (err as { message?: string })?.message,
      });
      return;
    }
    if (this.deps.host.isDestroyed()) return;
    let lastSeenRev: string | undefined;
    try {
      lastSeenRev = await this.deps.changelog.readLastSeenRevAsync();
    } catch (err) {
      // could not look is not "never seen anything": leave the card as it is
      // rather than deciding on an answer the database never gave
      this.reportProfileFailure('WhatsNewCardLastSeenReadFailed', err);
      return;
    }
    if (this.deps.host.isDestroyed()) return;

    const show = shouldShowWhatsNewCard(changelogRev, lastSeenRev);
    // keep the app-wide flags truthful for the dismissal write and the update
    // card's yield path
    this.deps.flags.setShowChangelogNotification(show);
    this.deps.flags.setChangelogRev(changelogRev);
    this.wantsToShow = show;
    if (show) this.version = this.deps.readableAppVersion();
    markNoticeHostForCheck(this.deps.host);
  }

  /**
   * Takes the dashboard slot back when the store update prompt is dismissed.
   *
   * Without this the slot stays empty even when a changelog nudge is waiting: the
   * only other thing that sets this card up runs once per dashboard load, and the
   * update prompt was drawn over it at the time.
   *
   * Called by the update notice through a callback the component wires. It was a
   * direct field write from that card's dismiss handler before #5236; making it a
   * named method is what turns an invisible cross-reach between two cards into an
   * edge somebody can find.
   */
  async reclaimSlotAsync(): Promise<void> {
    if (!this.deps.flags.getShowChangelogNotification()) return;
    let lastSeenRev: string | undefined;
    try {
      lastSeenRev = await this.deps.changelog.readLastSeenRevAsync();
    } catch (err) {
      this.reportProfileFailure('WhatsNewCardLastSeenReadFailed', err);
      return;
    }
    if (this.deps.host.isDestroyed()) return;
    if (!lastSeenRev) return;
    this.wantsToShow = true;
    this.version = this.deps.readableAppVersion();
  }

  /** Opens the changelog, which is also what marks it as read. */
  onOpenClick(): void {
    this.deps.trackWhatsNewOpened(this.version);
    this.deps.openChangelogPage();
  }

  /**
   * Dismissing is the same as having read the changelog: it stores this
   * revision as the last one seen, exactly as visiting the changelog page does.
   */
  async onDismissedAsync(): Promise<void> {
    this.wantsToShow = false;
    this.deps.flags.setShowChangelogNotification(false);
    const changelogRev = this.deps.flags.getChangelogRev();
    if (changelogRev) {
      try {
        await this.deps.changelog.saveLastSeenRevAsync(changelogRev);
      } catch (err) {
        // nothing was recorded, so the card may come back on a later load; that is
        // better than writing a stand-in profile over one we could not read
        this.reportProfileFailure('WhatsNewCardLastSeenSaveFailed', err);
      }
      if (this.deps.host.isDestroyed()) return;
    }
    // guarded so NG0911 cannot fire on a view destroyed during the save above
    markNoticeHostForCheck(this.deps.host);
  }

  /** Releases the changelog watcher, if one was ever created. */
  dispose(): void {
    this.deps.changelog.release();
  }

  /** Reports a profile read or write failure, unless it is an expected, non-actionable kind. */
  private reportProfileFailure(name: string, err: unknown): void {
    if (this.deps.changelog.isExpectedProfileFailure(err)) return;
    this.deps.trackSilentProblem(name, {
      name: (err as { name?: string })?.name,
      message: (err as { message?: string })?.message,
    });
  }
}
